import React, { useEffect, useMemo, useState } from "react";
import { Stethoscope, Search, Pencil } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/layout/PageHeader";
import { api, formatApiError } from "@/lib/api";
import { timeAgo } from "@/pages/fleet/constants";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const WRITE_ROLES = ["admin", "hah_coordinator", "care_coordinator", "clinical_director"];

export default function FleetClinicians() {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role_code);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [skillOptions, setSkillOptions] = useState([]); // [{value,label}]
  const [editing, setEditing] = useState(null);         // clinician being edited
  const [draftSkills, setDraftSkills] = useState([]);
  const [saving, setSaving] = useState(false);

  async function loadClinicians() {
    try { const r = await api.get("/fleet/clinicians"); setItems(r.data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadClinicians(); }, []);
  useEffect(() => {
    (async () => {
      try {
        const cats = await api.get("/lookups/categories");
        const cat = (cats.data.items || cats.data || []).find((c) => c.code === "clinical_skills");
        if (!cat) return;
        const vals = await api.get("/lookups/values", { params: { category_id: cat.id, status: "active", page_size: 200 } });
        setSkillOptions((vals.data.items || []).map((v) => ({ value: v.value, label: v.label })));
      } catch { /* skills lookup optional */ }
    })();
  }, []);

  const skillLabel = useMemo(() => {
    const m = {}; skillOptions.forEach((o) => { m[o.value] = o.label; }); return m;
  }, [skillOptions]);

  const filtered = items.filter((c) =>
    !search || (`${c.name} ${c.role_code} ${c.facility_name || ""}`).toLowerCase().includes(search.toLowerCase())
  );

  function openEdit(c) { setEditing(c); setDraftSkills([...(c.skills || [])]); }
  function toggleSkill(v) { setDraftSkills((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]); }

  async function saveSkills() {
    setSaving(true);
    try {
      await api.patch(`/fleet/clinicians/${editing.clinician_id}/skills`, { skills: draftSkills });
      toast.success(`Skills updated for ${editing.name}`);
      setEditing(null); loadClinicians();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="fleet-clinicians-page">
      <PageHeader title="Clinician Availability" subtitle="Workload, capacity and skills for all field clinicians." />
      <div className="hah-card p-3 mb-4 flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F5F7FA] border border-[#E5E7EB] rounded-lg flex-1">
          <Search className="w-4 h-4 text-[#94A3B8]" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search clinicians…"
            className="bg-transparent outline-none text-sm w-full" data-testid="fleet-clin-search" />
        </div>
      </div>

      {loading ? (
        <div className="hah-card p-8 text-center text-sm text-[#6B7280]">Loading…</div>
      ) : (
        <div className="hah-card p-2">
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead className="bg-[#F5F7FA] text-[10px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="text-left p-2.5">Clinician</th>
                <th className="text-left p-2.5">Role</th>
                <th className="text-left p-2.5">Specialty</th>
                <th className="text-left p-2.5">Skills</th>
                <th className="text-left p-2.5">Workload</th>
                <th className="text-left p-2.5">Updated</th>
                {canWrite && <th className="text-left p-2.5"></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const spec = [c.medical_specialization, c.sub_specialty].filter(Boolean).join(" · ");
                return (
                  <tr key={c.clinician_id} className="border-t border-[#E5E7EB] hover:bg-[#FAFBFC]" data-testid={`fleet-clin-row-${c.clinician_id}`}>
                    <td className="p-2.5 font-medium text-[#1F2937]">{c.name}</td>
                    <td className="p-2.5 text-[#6B7280] capitalize">{(c.role_code || "").replace(/_/g, " ")}</td>
                    <td className="p-2.5 text-[#6B7280] text-xs">{spec || "—"}</td>
                    <td className="p-2.5">
                      <div className="flex flex-wrap gap-1 max-w-[280px]">
                        {(c.skills || []).length === 0 ? <span className="text-[#CBD5E1] text-xs">No skills set</span>
                          : c.skills.map((s) => (
                            <span key={s} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-[#0B7E8C14] text-[#0B7E8C]">{skillLabel[s] || s}</span>
                          ))}
                      </div>
                    </td>
                    <td className="p-2.5 text-[#6B7280]">{c.workload}/{c.capacity || 6}</td>
                    <td className="p-2.5 text-[#94A3B8] text-xs">{timeAgo(c.updated_at)}</td>
                    {canWrite && (
                      <td className="p-2.5">
                        <button onClick={() => openEdit(c)} data-testid={`fleet-clin-edit-${c.clinician_id}`}
                          className="text-[12px] text-[#0B7E8C] hover:underline inline-flex items-center gap-1">
                          <Pencil className="w-3.5 h-3.5" /> Skills
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="fleet-clin-skills-dialog">
          <DialogHeader>
            <DialogTitle>Edit skills — {editing?.name}</DialogTitle>
            <DialogDescription>
              Select the clinical competencies this provider can perform. These drive skill-matching in Service Assignment (auto & manual).
              Medical specialty &amp; sub-specialty are managed in <span className="font-medium">User Management</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2 py-2 max-h-72 overflow-auto">
            {skillOptions.length === 0 && <div className="text-sm text-[#6B7280] col-span-2">No skills defined. Add options under Lookups → "Clinical Skills".</div>}
            {skillOptions.map((o) => {
              const checked = draftSkills.includes(o.value);
              return (
                <label key={o.value} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm ${checked ? "border-[#0B7E8C] bg-[#0B7E8C0A]" : "border-[#E5E7EB]"}`}
                  data-testid={`fleet-clin-skill-opt-${o.value}`}>
                  <input type="checkbox" checked={checked} onChange={() => toggleSkill(o.value)} className="rounded" />
                  <span className="text-[#1F2937]">{o.label}</span>
                </label>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#E5E7EB]">Cancel</Button>
            <Button onClick={saveSkills} disabled={saving} className="bg-[#0B7E8C] hover:bg-[#128C97] text-white" data-testid="fleet-clin-skills-save">
              {saving ? "Saving…" : "Save skills"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
